import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { heroBanners } from '@/mocks/home';

export default function Hero() {
  const [current, setCurrent] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const goTo = useCallback(
    (index: number) => {
      if (isTransitioning) return;
      setIsTransitioning(true);
      setCurrent(index);
      setTimeout(() => setIsTransitioning(false), 600);
    },
    [isTransitioning],
  );

  const next = useCallback(() => {
    goTo((current + 1) % heroBanners.length);
  }, [current, goTo]);

  useEffect(() => {
    const timer = setInterval(next, 5000);
    return () => clearInterval(timer);
  }, [next]);

  return (
    <section
      className="relative w-full h-[420px] md:h-[600px] overflow-hidden"
      aria-label="精彩推荐"
    >
      {heroBanners.map((banner, index) => (
        <div
          key={banner.id}
          className={`absolute inset-0 transition-opacity duration-600 ${
            index === current ? 'opacity-100 z-10' : 'opacity-0 z-0'
          }`}
        >
          <img
            src={banner.imageUrl}
            alt={banner.title}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/50" />
        </div>
      ))}

      <div className="absolute inset-0 z-20 flex items-center">
        <div className="max-w-[1400px] mx-auto w-full px-4 md:px-6">
          <div className="max-w-xl">
            <span className="inline-block px-3 py-1 rounded-full bg-primary-500/90 text-background-50 text-xs font-medium mb-4">
              {heroBanners[current].category}
            </span>
            <h1 className="text-3xl md:text-5xl font-heading font-bold text-background-50 mb-3 leading-tight">
              {heroBanners[current].title}
            </h1>
            <p className="text-sm md:text-base text-background-200 mb-6 leading-relaxed">
              {heroBanners[current].subtitle}
            </p>
            <div className="flex items-center gap-4">
              <Link
                to={heroBanners[current].link}
                className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-play-fill text-base"></i>
                </div>
                立即观看
              </Link>
              <span className="flex items-center gap-1 text-background-200 text-sm">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-star-fill text-accent-400 text-sm"></i>
                </div>
                {heroBanners[current].rating}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-6 left-0 right-0 z-20 flex items-center justify-center gap-2">
        {heroBanners.map((_, index) => (
          <button
            key={index}
            onClick={() => goTo(index)}
            className={`w-2.5 h-2.5 rounded-full transition-all duration-300 cursor-pointer ${
              index === current
                ? 'bg-primary-500 w-8'
                : 'bg-background-50/40 hover:bg-background-50/70'
            }`}
            aria-label={`切换到第 ${index + 1} 张`}
          />
        ))}
      </div>

      <button
        onClick={() => goTo((current - 1 + heroBanners.length) % heroBanners.length)}
        className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 z-20 w-8 h-8 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-background-50/20 hover:bg-background-50/40 backdrop-blur-sm transition-colors cursor-pointer"
        aria-label="上一张"
      >
        <i className="ri-arrow-left-s-line text-lg text-background-50"></i>
      </button>
      <button
        onClick={next}
        className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 z-20 w-8 h-8 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-background-50/20 hover:bg-background-50/40 backdrop-blur-sm transition-colors cursor-pointer"
        aria-label="下一张"
      >
        <i className="ri-arrow-right-s-line text-lg text-background-50"></i>
      </button>
    </section>
  );
}